import constants from '@/shared/constants/constants';
import iconv from 'iconv-lite';
import JSZip from 'jszip';
import { fetchGenerationHistoryImageBlob } from '@/services/generationHistory.service';
import { fetchDesignBoardProjectPdfBlob } from '@/services/designBoard.service';
import { dateFormatted } from '@/shared/filters';
import { sanitizeDownloadFileName } from '@/shared/functions';

const REQUEST_DATETIME_FORMAT = 'YYYY/MM/DD HH:mm';

const META_LABEL_KEYS = {
  product_category: 'generation_history.meta.product_category',
  conversation_instruction: 'generation_history.meta.conversation_instruction',
  revision_instruction: 'generation_history.meta.revision_instruction',
  trends_to_incorporate: 'generation_history.meta.trends_to_incorporate',
  challenges_to_address: 'generation_history.meta.challenges_to_address',
  unique_uses: 'generation_history.meta.unique_uses',
  technologies_to_leverage: 'generation_history.meta.technologies_to_leverage',
  brands_to_promote: 'generation_history.meta.brands_to_promote',
  target_audience: 'generation_history.meta.target_audience',
  product_features_services: 'generation_history.meta.product_features_services',
  other: 'generation_history.meta.other',
  number_of_generations: 'generation_history.meta.number_of_generations',
  menu: 'generation_history.meta.menu',
  prompt: 'generation_history.meta.prompt',
  category_shape: 'generation_history.meta.category_shape',
  concept: 'generation_history.meta.concept',
  brand: 'generation_history.meta.brand',
  company: 'generation_history.meta.company',
  mission: 'generation_history.meta.mission',
  category: 'generation_history.meta.category',
  other_conditions: 'generation_history.meta.other_conditions',
  image_size: 'generation_history.meta.image_size',
  ai_model: 'generation_history.meta.ai_model',
  request_datetime: 'generation_history.meta.request_datetime',
};

const EDIT_CONCEPT_PRESET_LABEL_KEYS = {
  [constants.EDIT_CONCEPT_TYPE_DETAIL]: 'edit_concept_modal.preset.detail',
  [constants.EDIT_CONCEPT_TYPE_COMPANY_UNIQUE]: 'edit_concept_modal.preset.company_unique',
  [constants.EDIT_CONCEPT_TYPE_VARIATION]: 'edit_concept_modal.preset.variation',
  [constants.EDIT_CONCEPT_TYPE_BRUSH_UP]: 'edit_concept_modal.preset.brush_up',
  [constants.EDIT_CONCEPT_TYPE_COMPANY_CONCEPT_RULE]: 'edit_concept_modal.preset.company_rule',
};

const CONCEPT_LIST_META_KEYS = [
  'product_category',
  'trends_to_incorporate',
  'challenges_to_address',
  'unique_uses',
  'technologies_to_leverage',
  'brands_to_promote',
];

const NAMING_LIST_META_KEYS = [
  'product_category',
  'target_audience',
  'product_features_services',
  'other',
];

const DESIGN_LIST_META_KEYS = [
  'menu',
  'prompt',
  'ai_model',
  'image_size',
];

const DESIGN_BOARD_LIST_META_KEYS = [
  'mission',
  'category',
  'brand',
  'company',
  'other',
];

const DESIGN_KEY_VISUAL_ASSIST_LIST_META_KEYS = [
  'menu',
  'category_shape',
  'concept',
  'brand',
  'company',
  'ai_model',
  'image_size',
];

const DESIGN_KEY_VISUAL_ASSIST_DOWNLOAD_KEYS = [
  'category_shape',
  'concept',
  'brand',
  'company',
];

const AI_MODEL_DISPLAY_MAP = {
  [constants.MODEL_SDXL]: 'SDXL',
  [constants.MODEL_SD3]: 'SD 3.5 Large',
  [constants.MODEL_GPT_IMAGE_1]: 'GPT Image 1',
  [constants.MODEL_GPT_IMAGE_1_5]: 'GPT Image 1.5',
  [constants.MODEL_GPT_IMAGE_2]: 'GPT Image 2',
  [constants.MODEL_GPT_IMAGE_2_5_SUNBURST]: 'GPT Image 2.5 Sunburst',
  [constants.MODEL_DALL_E3]: 'DALL-E3',
  [constants.MODEL_FLUX_PRO]: 'FLUX.2',
  'flux-1.1-pro': 'Flux 1.1 Pro',
  [constants.MODEL_GEMINI]: 'Gemini Nano Banana 2.1',
  [constants.MODEL_GEMINI_3_PRO]: 'Gemini 3 Pro Image [Nano Banana Pro]',
  'gemini 2.5 flash image': 'Gemini 2.5 Flash Image [Nano Banana]',
  'dall e-3': 'DALL-E3',
  'flux 1.1 pro': 'Flux 1.1 Pro',
  'gpt-image-1': 'GPT Image 1',
  'gpt-image-2.5-sunburst': 'GPT Image 2.5 Sunburst',
};

const CONCEPT_MODEL_DISPLAY_MAP = {
  'GPT-5.5': 'GPT-5.5',
  'Gemini 3.5 Flash': 'Gemini 3.5 Flash',
  'Claude 4.6 Sonnet': 'Claude 4.6 Sonnet',
};

export function formatMetaValue(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  return String(value);
}

export function formatAiModelDisplay(modelKey, projectType, inputMeta) {
  const raw = formatMetaValue(modelKey);
  if (!raw) {
    return '';
  }
  if (isConceptProjectType(projectType) || isNamingProjectType(projectType)) {
    return CONCEPT_MODEL_DISPLAY_MAP[raw] || raw;
  }
  if (isDesignProjectType(projectType)) {
    return AI_MODEL_DISPLAY_MAP[raw] || raw;
  }
  return raw;
}

export function getServiceNameKey(projectType) {
  if (projectType === constants.PROJECT_GENERATE_CONCEPT) {
    return 'generation_history.service_concept';
  }
  if (projectType === constants.PROJECT_CHAT_GPT) {
    return 'generation_history.service_naming';
  }
  if (projectType === constants.PROJECT_GENERATE_IMAGE) {
    return 'generation_history.service_design';
  }
  if (projectType === constants.PROJECT_DESIGN_BOARD) {
    return 'generation_history.service_design_board';
  }
  return null;
}

function getChatGptMessageType(item) {
  if (!item) {
    return null;
  }
  if (
    item.message_type === constants.TYPE_NAMING
    || item.message_type === constants.TYPE_CATCHPHRASE
  ) {
    return item.message_type;
  }
  const outputs = item.preview_outputs || [];
  const preview = outputs.find((row) => row.item_type != null);
  if (!preview) {
    return null;
  }
  if (preview.item_type === constants.GENERATION_HISTORY_FAVORITE_ITEM_CATCHCOPY) {
    return constants.TYPE_CATCHPHRASE;
  }
  if (preview.item_type === constants.GENERATION_HISTORY_FAVORITE_ITEM_NAMING) {
    return constants.TYPE_NAMING;
  }
  return null;
}

export function getViewAllLinkKey(projectType, item) {
  if (projectType === constants.PROJECT_GENERATE_CONCEPT) {
    return 'generation_history.view_all_concepts';
  }
  if (projectType === constants.PROJECT_CHAT_GPT) {
    if (getChatGptMessageType(item) === constants.TYPE_CATCHPHRASE) {
      return 'generation_history.view_all_copy';
    }
    return 'generation_history.view_all_naming';
  }
  if (projectType === constants.PROJECT_GENERATE_IMAGE) {
    return 'generation_history.view_all_images';
  }
  if (projectType === constants.PROJECT_DESIGN_BOARD) {
    return 'generation_history.view_all_boards';
  }
  return null;
}

export function isDesignProjectType(projectType) {
  return projectType === constants.PROJECT_GENERATE_IMAGE;
}

export function isDesignBoardProjectType(projectType) {
  return projectType === constants.PROJECT_DESIGN_BOARD;
}

export function getDesignBoardBoardLabelKey(itemType) {
  if (itemType === constants.GENERATION_HISTORY_ITEM_DESIGN_BOARD_BRAND) {
    return 'design_board.brand_board';
  }
  if (itemType === constants.GENERATION_HISTORY_ITEM_DESIGN_BOARD_MOOD) {
    return 'design_board.mood_board';
  }
  return null;
}

export function formatDesignBoardDeliveryScheduleText(inputMeta, t) {
  const hour = inputMeta && inputMeta.delivery_run_hour;
  if (hour === null || hour === undefined || hour === '') {
    return '';
  }
  return t('generation_history.meta.delivery_hour_send', { n: hour });
}

export function isKeyVisualAssistMeta(inputMeta) {
  if (!inputMeta) {
    return false;
  }
  return Number(inputMeta.menu) === constants.GENERATE_IMAGE_MENU_KEY_VISUAL
    && !!inputMeta.is_generated_prompt;
}

export function isConceptProjectType(projectType) {
  return projectType === constants.PROJECT_GENERATE_CONCEPT;
}

export function isNamingProjectType(projectType) {
  return projectType === constants.PROJECT_CHAT_GPT;
}

export function getListLeftPreview(item, projectType) {
  const outputs = item.preview_outputs || [];
  if (isNamingProjectType(projectType)) {
    const firstText = outputs.find((row) => row.type === 'text' && row.text);
    return {
      mode: 'text',
      text: firstText ? firstText.text : '',
      images: [],
    };
  }
  if (isConceptProjectType(projectType)) {
    const images = outputs
      .filter((row) => row.type === 'image' && row.url)
      .slice(0, 2)
      .map((row, index) => ({
        url: row.url,
        itemId: row.item_id,
        itemType: row.item_type,
        posterIndex: index,
      }));
    const firstText = outputs.find((row) => row.type === 'text' && row.text);
    const text = firstText ? firstText.text : '';
    if (images.length) {
      return {
        mode: 'image',
        text,
        images,
      };
    }
    return {
      mode: 'text',
      text,
      images: [],
    };
  }
  if (isDesignProjectType(projectType)) {
    const images = outputs
      .filter((row) => row.type === 'image' && row.url)
      .slice(0, 4)
      .map((row) => ({
        url: row.url,
        itemId: row.item_id,
        itemType: row.item_type,
        posterIndex: 0,
      }));
    return { mode: 'images', text: '', images };
  }
  if (isDesignBoardProjectType(projectType)) {
    const images = outputs
      .filter((row) => row.type === 'image' && row.url)
      .slice(0, 2)
      .map((row) => ({
        url: row.url,
        itemId: row.item_id,
        itemType: row.item_type,
        posterIndex: 0,
      }));
    return { mode: 'images', text: '', images };
  }
  return { mode: 'empty', text: '', images: [] };
}

export function formatDesignMenuLabel(menu, t) {
  // menu=0 (remove-background / non-txt2img): hide menu label
  if (menu === null || menu === undefined || menu === '' || Number(menu) === 0) {
    return '';
  }
  const menuMap = {
    [constants.GENERATE_IMAGE_MENU_PACKAGE]: 'sd.menu_package',
    [constants.GENERATE_IMAGE_MENU_PRODUCT]: 'sd.menu_product',
    [constants.GENERATE_IMAGE_MENU_KEY_VISUAL]: 'sd.menu_key_visual',
    [constants.GENERATE_IMAGE_MENU_FREE]: 'sd.menu_free',
  };
  const key = menuMap[Number(menu)];
  return key ? t(key) : formatMetaValue(menu);
}

export function formatDesignImageSizeLabel(inputMeta, t) {
  const width = Number(inputMeta && inputMeta.width);
  const height = Number(inputMeta && inputMeta.height);
  if (width && height) {
    if (width < height) {
      return t('sd.portrait');
    }
    return width === height ? t('sd.square') : t('sd.landscape');
  }
  return formatMetaValue(inputMeta && inputMeta.image_size);
}

function resolveMetaValue(key, inputMeta, t, projectType) {
  if (!inputMeta) {
    return '';
  }
  if (key === 'menu') {
    return formatDesignMenuLabel(inputMeta.menu, t);
  }
  if (key === 'image_size') {
    return formatDesignImageSizeLabel(inputMeta, t);
  }
  if (key === 'ai_model') {
    return formatAiModelDisplay(inputMeta.ai_model, projectType, inputMeta);
  }
  return formatMetaValue(inputMeta[key]);
}

function buildInlineFields(keys, inputMeta, t, projectType) {
  const fields = [];
  keys.forEach((key) => {
    const value = resolveMetaValue(key, inputMeta, t, projectType);
    if (!value) {
      return;
    }
    fields.push({
      key,
      labelKey: META_LABEL_KEYS[key],
      value,
    });
  });
  return fields;
}

function appendGenerationCountField(fields, totalCount) {
  if (!totalCount) {
    return fields;
  }
  fields.push({
    key: 'number_of_generations',
    labelKey: META_LABEL_KEYS.number_of_generations,
    value: String(totalCount),
  });
  return fields;
}

function isEditConceptContext(itemOrCard, inputMeta) {
  if (itemOrCard && Number(itemOrCard.item_type) === constants.GENERATION_HISTORY_FAVORITE_ITEM_EDIT_CONCEPT) {
    return true;
  }
  if (!inputMeta || inputMeta.edit_type === null || inputMeta.edit_type === undefined || inputMeta.edit_type === '') {
    return false;
  }
  return true;
}

function resolveRevisionInstructionValue(inputMeta, t) {
  if (!inputMeta) {
    return '';
  }
  const editType = Number(inputMeta.edit_type);
  if (editType === constants.EDIT_CONCEPT_TYPE_FREE_TEXT) {
    return formatMetaValue(inputMeta.prompt);
  }
  const presetKey = EDIT_CONCEPT_PRESET_LABEL_KEYS[editType];
  if (presetKey) {
    return t(presetKey);
  }
  return formatMetaValue(inputMeta.prompt);
}

function buildRevisionInstructionField(inputMeta, t) {
  const value = resolveRevisionInstructionValue(inputMeta, t);
  if (!value) {
    return null;
  }
  return {
    key: 'revision_instruction',
    labelKey: META_LABEL_KEYS.revision_instruction,
    value,
  };
}

function insertFieldBeforeAiModel(fields, field) {
  if (!field) {
    return fields;
  }
  const aiIndex = fields.findIndex((row) => row.key === 'ai_model');
  if (aiIndex === -1) {
    fields.push(field);
    return fields;
  }
  fields.splice(aiIndex, 0, field);
  return fields;
}

function applyNamingConversationLabel(fields, useConversationLabel) {
  if (!useConversationLabel) {
    return fields;
  }
  return fields.map((field) => {
    if (field.key !== 'product_category') {
      return field;
    }
    return {
      ...field,
      labelKey: META_LABEL_KEYS.conversation_instruction,
    };
  });
}

export function getListInlineMetaFields(item, t) {
  const { project_type: projectType, input_meta: inputMeta, total_count: totalCount } = item;
  if (isConceptProjectType(projectType)) {
    const metaFields = buildInlineFields(CONCEPT_LIST_META_KEYS, inputMeta, t, projectType);
    const aiFields = buildInlineFields(['ai_model'], inputMeta, t, projectType);
    let fields = metaFields.concat(aiFields);
    if (isEditConceptContext(item, inputMeta)) {
      fields = insertFieldBeforeAiModel(fields, buildRevisionInstructionField(inputMeta, t));
    }
    return appendGenerationCountField(fields, totalCount);
  }
  if (isNamingProjectType(projectType)) {
    const metaFields = buildInlineFields(NAMING_LIST_META_KEYS, inputMeta, t, projectType);
    const aiFields = buildInlineFields(['ai_model'], inputMeta, t, projectType);
    const useConversationLabel = Number(totalCount) >= 2;
    return applyNamingConversationLabel(metaFields.concat(aiFields), useConversationLabel);
  }
  if (isDesignProjectType(projectType)) {
    const keys = isKeyVisualAssistMeta(inputMeta)
      ? DESIGN_KEY_VISUAL_ASSIST_LIST_META_KEYS
      : DESIGN_LIST_META_KEYS;
    const fields = buildInlineFields(keys, inputMeta, t, projectType);
    return appendGenerationCountField(fields, totalCount);
  }
  if (isDesignBoardProjectType(projectType)) {
    return buildDesignBoardInlineFields(inputMeta, t, projectType);
  }
  return [];
}

function buildDesignBoardScheduleField(inputMeta, t) {
  const text = formatDesignBoardDeliveryScheduleText(inputMeta, t);
  if (!text) {
    return null;
  }
  return {
    key: 'delivery_schedule',
    labelOnly: true,
    value: text,
  };
}

function buildDesignBoardInlineFields(inputMeta, t, projectType) {
  const fields = buildInlineFields(DESIGN_BOARD_LIST_META_KEYS, inputMeta, t, projectType).map(
    (field) => (
      field.key === 'other'
        ? { ...field, labelKey: META_LABEL_KEYS.other_conditions }
        : field
    ),
  );
  const scheduleField = buildDesignBoardScheduleField(inputMeta, t);
  if (scheduleField) {
    fields.push(scheduleField);
  }
  return fields;
}

export function getDesignBoardDetailMetaFields(card, projectType, t) {
  const inputMeta = (card && card.input_meta) || {};
  return buildDesignBoardInlineFields(inputMeta, t, projectType);
}

export function buildDesignBoardDownloadTextContent(card, projectType, t) {
  const fields = getDesignBoardDetailMetaFields(card, projectType, t);
  const lines = [];
  fields.forEach((field) => {
    if (field.labelOnly) {
      return;
    }
    const label = t(field.labelKey);
    if (!label && !field.value) {
      return;
    }
    if (lines.length) {
      lines.push('');
    }
    lines.push(label);
    lines.push(field.value);
  });
  return lines.join('\n');
}

export function getDetailBodyMetaFields(card, projectType, t, options = {}) {
  let keys = [];
  if (isConceptProjectType(projectType)) {
    keys = CONCEPT_LIST_META_KEYS;
  } else if (isNamingProjectType(projectType)) {
    keys = NAMING_LIST_META_KEYS;
  }
  const inputMeta = card.input_meta;
  let fields = buildInlineFields(keys, inputMeta, t, projectType);
  const aiFields = buildInlineFields(['ai_model'], inputMeta, t, projectType);
  fields.push(...aiFields);
  if (isConceptProjectType(projectType) && isEditConceptContext(card, inputMeta)) {
    fields = insertFieldBeforeAiModel(fields, buildRevisionInstructionField(inputMeta, t));
  }
  if (isNamingProjectType(projectType)) {
    const cardIndex = options.cardIndex != null ? Number(options.cardIndex) : 0;
    fields = applyNamingConversationLabel(fields, cardIndex >= 1);
  }
  if (card.request_datetime_ts) {
    fields.push({
      key: 'request_datetime_ts',
      labelKey: META_LABEL_KEYS.request_datetime,
      value: dateFormatted(card.request_datetime_ts, REQUEST_DATETIME_FORMAT),
    });
  }
  return fields;
}

function extractTitleLine(text) {
  return String(text || '').split('\n').map((line) => line.trim()).find(Boolean) || '';
}

function truncateTitle(text) {
  return text.length > 60 ? `${text.slice(0, 60)}…` : text;
}

export function getCardTitle(card, t) {
  const inputMeta = card && card.input_meta;
  if (isEditConceptContext(card, inputMeta)) {
    const sourceTitle = extractTitleLine(inputMeta && inputMeta.source_title);
    const suffix = extractTitleLine(
      typeof t === 'function'
        ? resolveRevisionInstructionValue(inputMeta, t)
        : formatMetaValue(inputMeta && inputMeta.prompt)
    );
    const title = [sourceTitle, suffix].filter(Boolean).join(' ');
    if (title) {
      return truncateTitle(title);
    }
  }
  return truncateTitle(extractTitleLine((card && card.output && card.output.text) || ''));
}

export function getConceptCardImageUrls(card) {
  if (!card) {
    return [];
  }
  const urls = [];
  if (Array.isArray(card.poster_urls)) {
    card.poster_urls.forEach((url) => {
      if (url && !urls.includes(url)) {
        urls.push(url);
      }
    });
  }
  if (!urls.length && card.output && card.output.url) {
    urls.push(card.output.url);
  }
  return urls.slice(0, 2);
}

export function normalizePosterIndex(posterIndex, fallback = 0) {
  const indexed = Number(posterIndex);
  return Number.isFinite(indexed) ? indexed : fallback;
}

export function getConceptCardPosterIndex(card) {
  if (!card) {
    return 0;
  }
  if (card._concept_poster_index != null) {
    return normalizePosterIndex(card._concept_poster_index);
  }
  if (card.poster_index != null) {
    return normalizePosterIndex(card.poster_index);
  }
  return 0;
}

export function getConceptDisplayCardKey(card) {
  if (card && card._concept_display_key) {
    return card._concept_display_key;
  }
  const posterIndex = getConceptCardPosterIndex(card);
  return `${card.item_type}-${card.item_id}-poster-${posterIndex}`;
}

export function isConceptPosterFavorite(card, posterIndex = 0) {
  if (!card) {
    return false;
  }
  const index = normalizePosterIndex(posterIndex);
  const indexes = Array.isArray(card.favorite_poster_indexes)
    ? card.favorite_poster_indexes.map((value) => normalizePosterIndex(value))
    : [];
  const textIndex = constants.GENERATION_HISTORY_FAVORITE_POSTER_INDEX_TEXT;
  const urls = getConceptCardImageUrls(card);
  if (index === textIndex) {
    if (indexes.includes(textIndex)) {
      return true;
    }
    // Legacy: 0 meant text ★ when no posters existed
    return !urls.length && indexes.includes(0);
  }
  if (indexes.length) {
    return indexes.includes(index);
  }
  // Legacy fallback without favorite_poster_indexes array
  return !!card.is_favorite && index === 0;
}

export function expandConceptDetailCards(cards, projectType = null) {
  if (!Array.isArray(cards)) {
    return [];
  }
  const textIndex = constants.GENERATION_HISTORY_FAVORITE_POSTER_INDEX_TEXT;
  const expanded = cards.reduce((acc, card) => {
    const type = projectType != null ? projectType : card.project_type;
    if (!isConceptProjectType(type)) {
      acc.push(card);
      return acc;
    }
    // Already expanded (or favorites-list single-slot card with poster_index set).
    if (card._concept_display_key) {
      acc.push(card);
      return acc;
    }
    if (card.poster_index != null) {
      const index = normalizePosterIndex(card.poster_index);
      const urls = index === textIndex ? [] : getConceptCardImageUrls(card);
      acc.push({
        ...card,
        poster_urls: urls,
        poster_index: index,
        _concept_poster_index: index,
        _concept_display_key: `${card.item_type}-${card.item_id}-poster-${index}`,
        is_favorite: isConceptPosterFavorite(card, index) || !!card.is_favorite,
      });
      return acc;
    }
    const urls = getConceptCardImageUrls(card);
    const posterTsList = Array.isArray(card.poster_datetime_ts)
      ? card.poster_datetime_ts
      : [];
    urls.forEach((url, index) => {
      const posterTs = Number(posterTsList[index]);
      // 0 is BE sentinel for missing activity_at — fall back to concept timestamp.
      const hasValidPosterTs = Number.isFinite(posterTs) && posterTs !== 0;
      acc.push({
        ...card,
        poster_urls: [url],
        poster_index: index,
        _concept_poster_index: index,
        _concept_display_key: `${card.item_type}-${card.item_id}-poster-${index}`,
        is_favorite: isConceptPosterFavorite(card, index),
        request_datetime_ts: hasValidPosterTs
          ? posterTs
          : card.request_datetime_ts,
      });
    });
    // Text card keeps concept/edit created_at (do not inherit poster time).
    acc.push({
      ...card,
      poster_urls: [],
      poster_index: textIndex,
      _concept_poster_index: textIndex,
      _concept_display_key: `${card.item_type}-${card.item_id}-poster-${textIndex}`,
      is_favorite: isConceptPosterFavorite(card, textIndex),
    });
    return acc;
  }, []);
  // Sort each display card independently by its own request_datetime_ts.
  return expanded.sort(
    (a, b) => (Number(b.request_datetime_ts) || 0) - (Number(a.request_datetime_ts) || 0),
  );
}

export function getCardOutputBody(card) {
  const text = (card.output && card.output.text) || '';
  if (!text) {
    return '';
  }
  const lines = text.split('\n');
  const firstNonEmptyIndex = lines.findIndex((line) => line.trim());
  if (firstNonEmptyIndex === -1) {
    return '';
  }
  const body = lines.slice(firstNonEmptyIndex + 1).join('\n').trim();
  return body || text;
}

export function getCardCopyText(card, projectType) {
  if (isNamingProjectType(projectType)) {
    return removeAsterisks((card.output && card.output.text) || '');
  }
  if (isDesignBoardProjectType(projectType)) {
    return formatMetaValue((card.input_meta && card.input_meta.mission) || '');
  }
  return getCardOutputBody(card);
}

export function getDesignCopyPromptText(card) {
  const inputMeta = (card && card.input_meta) || {};
  if (isKeyVisualAssistMeta(inputMeta)) {
    return inputMeta.concept || '';
  }
  return inputMeta.prompt || '';
}

export function getDesignDownloadTextContent(card, t) {
  const inputMeta = (card && card.input_meta) || {};
  if (!isKeyVisualAssistMeta(inputMeta)) {
    return inputMeta.prompt || '';
  }
  const lines = [];
  DESIGN_KEY_VISUAL_ASSIST_DOWNLOAD_KEYS.forEach((key) => {
    const value = formatMetaValue(inputMeta[key]);
    if (!value) {
      return;
    }
    if (lines.length) {
      lines.push('');
    }
    if (typeof t === 'function') {
      lines.push(t(META_LABEL_KEYS[key]));
    }
    lines.push(value);
  });
  return lines.join('\n');
}

function firstTenChars(text) {
  if (!text) {
    return '';
  }
  const collapsed = String(text).replace(/[\r\n\t]+/g, ' ').trim();
  return sanitizeDownloadFileName(collapsed.slice(0, 10), 10);
}

function sanitizeFilenameBase(text, maxLen = 100) {
  const collapsed = String(text || '').replace(/[\r\n\t]+/g, ' ').trim();
  return sanitizeDownloadFileName(collapsed, maxLen);
}

export function buildDesignDownloadNames(prompt, listIndex) {
  const snippet = firstTenChars(prompt) || 'prompt';
  const sequence = listIndex == null || listIndex === '' ? null : (Number(listIndex) || 1);
  const suffix = sequence == null ? '' : `_${sequence}`;
  return {
    zipName: sanitizeFilenameBase(`生成リスト_デザイン生成_(${snippet})${suffix}`),
    imageName: sanitizeFilenameBase(`生成リスト_デザイン生成_(${snippet})${suffix}`),
    promptName: sanitizeFilenameBase(`生成リスト_デザイン生成プロンプト_(${snippet})`),
  };
}

export function buildConceptDownloadNames(outputText, listIndex) {
  const snippet = firstTenChars(outputText) || 'output';
  const base = sanitizeFilenameBase(`生成リスト_コンセプト生成_(${snippet})`);
  const sequence = listIndex == null || listIndex === '' ? null : (Number(listIndex) || 1);
  const suffix = sequence == null ? '' : `_${sequence}`;
  return {
    zipName: sanitizeFilenameBase(`${base}${suffix}`),
    imageName: base,
    outputName: sanitizeFilenameBase(`生成リスト_コンセプト生成_(${snippet})`),
  };
}

export function buildDownloadFilename(projectType, card, listIndex) {
  const prefix = '生成リスト_';
  const outputText = (card.output && card.output.text) || '';

  if (projectType === constants.PROJECT_GENERATE_CONCEPT) {
    const snippet = firstTenChars(outputText) || 'output';
    return sanitizeFilenameBase(`${prefix}コンセプト生成_(${snippet})`);
  }
  if (projectType === constants.PROJECT_CHAT_GPT) {
    const snippet = firstTenChars(outputText) || 'output';
    return sanitizeFilenameBase(`${prefix}ネーミング・キャッチコピー生成_(${snippet})`);
  }
  const sequence = listIndex || card.sequence_in_request || 1;
  return buildDesignDownloadNames(getDesignCopyPromptText(card), sequence).zipName;
}

export function getDownloadTextContent(projectType, card, t) {
  if (projectType === constants.PROJECT_GENERATE_IMAGE) {
    return getDesignDownloadTextContent(card, t);
  }
  return (card.output && card.output.text) || '';
}

export function downloadShiftJisText(filenameBase, text) {
  const encodedContent = iconv.encode(text || '', 'shift_jis');
  const blob = new Blob([encodedContent], { type: 'text/plain;charset=shift_jis' });
  const safeBase = sanitizeFilenameBase(filenameBase);
  const filename = safeBase.endsWith('.txt') ? safeBase : `${safeBase}.txt`;
  downloadBlob(blob, filename);
}

function downloadBlob(blob, filename) {
  const element = document.createElement('a');
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blob, filename);
    return;
  }
  element.href = window.URL.createObjectURL(blob);
  element.download = filename;
  element.style.display = 'none';
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
  window.URL.revokeObjectURL(element.href);
}

const DESIGN_BOARD_LABELS = {
  [constants.GENERATION_HISTORY_ITEM_DESIGN_BOARD_BRAND]: 'ブランドボード',
  [constants.GENERATION_HISTORY_ITEM_DESIGN_BOARD_MOOD]: 'ムードボード',
};

export function buildDesignBoardDownloadBaseName(card, projectName) {
  const label = DESIGN_BOARD_LABELS[card.item_type] || 'ボード';
  return sanitizeFilenameBase(`${label}_${projectName || ''}`);
}

export async function downloadDesignBoardZip(card, projectType, t, projectName, projectId) {
  const baseName = buildDesignBoardDownloadBaseName(card, projectName);
  const zip = new JSZip();
  const board = card.item_type === constants.GENERATION_HISTORY_ITEM_DESIGN_BOARD_MOOD ? 'mood' : 'brand';
  const { status, data: blob } = await fetchDesignBoardProjectPdfBlob(projectId, board);
  if (status !== constants.HTTP_OK || !blob) {
    return;
  }
  zip.file(`${baseName}.pdf`, blob);
  const text = buildDesignBoardDownloadTextContent(card, projectType, t);
  zip.file(`${baseName}.txt`, iconv.encode(text, 'shift_jis'));
  const content = await zip.generateAsync({ type: 'blob' });
  downloadBlob(content, `${baseName}.zip`);
}

export async function downloadDesignZip(card, listIndex, t) {
  const text = getDesignDownloadTextContent(card, t);
  const snippetSource = getDesignCopyPromptText(card) || text;
  const names = buildDesignDownloadNames(snippetSource, listIndex);
  const zip = new JSZip();
  const imageUrl = card.output && card.output.url;

  if (imageUrl) {
    let blob = null;
    if (card.item_id) {
      try {
        blob = await fetchGenerationHistoryImageBlob(card.item_id);
      } catch (e) {
        // fallback below
      }
    }
    if (!blob) {
      const response = await fetch(imageUrl, { mode: 'cors' });
      blob = await response.blob();
    }
    zip.file(`${names.imageName}.png`, blob);
  }

  zip.file(`${names.promptName}.txt`, iconv.encode(text, 'shift_jis'));

  const content = await zip.generateAsync({ type: 'blob' });
  downloadBlob(content, `${names.zipName}.zip`);
}

export async function downloadConceptZip(card, listIndex, posterIndex = null) {
  const outputText = (card.output && card.output.text) || '';
  const names = buildConceptDownloadNames(outputText, listIndex);
  const zip = new JSZip();
  const posterUrls = getConceptCardImageUrls(card);
  const resolvedIndex = posterIndex != null ? Number(posterIndex) || 0 : null;
  const downloadIndexes = resolvedIndex != null
    ? [resolvedIndex]
    : posterUrls.map((_, index) => index);

  for (let i = 0; i < downloadIndexes.length; i += 1) {
    const index = downloadIndexes[i];
    const url = resolvedIndex != null ? (posterUrls[0] || '') : posterUrls[index];
    if (!url && resolvedIndex == null) {
      continue;
    }
    let blob = null;
    if (card.item_id) {
      try {
        blob = await fetchGenerationHistoryImageBlob(card.item_id, {
          itemType: card.item_type,
          index,
        });
      } catch (e) {
        // fallback below
      }
    }
    if (!blob && url) {
      try {
        const response = await fetch(url, { mode: 'cors' });
        blob = await response.blob();
      } catch (e) {
        // skip this poster
      }
    }
    if (blob) {
      const suffix = resolvedIndex == null && posterUrls.length > 1 ? `_${index + 1}` : '';
      zip.file(`${names.imageName}${suffix}.png`, blob);
    }
  }

  const outputContent = iconv.encode(outputText, 'shift_jis');
  zip.file(`${names.outputName}.txt`, outputContent);

  const content = await zip.generateAsync({ type: 'blob' });
  downloadBlob(content, `${names.zipName}.zip`);
}

export async function copyTextToClipboard(text) {
  const value = text || '';
  if (navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
}

function waitForImageLoad(img) {
  if (img.complete && img.naturalWidth) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    img.addEventListener('load', resolve, { once: true });
    img.addEventListener('error', reject, { once: true });
  });
}

export async function imageElementToBlob(img) {
  if (!img || !(img instanceof HTMLImageElement)) {
    throw new Error('invalid_image_element');
  }
  await waitForImageLoad(img);
  if (!img.naturalWidth || !img.naturalHeight) {
    throw new Error('invalid_image_dimensions');
  }
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
        return;
      }
      reject(new Error('canvas_to_blob_failed'));
    }, 'image/png');
  });
}

async function writeImageBlobToClipboard(blob) {
  if (navigator.clipboard && window.ClipboardItem) {
    const type = blob.type || 'image/png';
    await navigator.clipboard.write([
      new window.ClipboardItem({ [type]: blob }),
    ]);
    return;
  }
  throw new Error('clipboard_unsupported');
}

async function resolveImageBlob(imageUrl, options = {}) {
  const { imageElement, resultId, itemType, index } = options;
  const imgEl = Array.isArray(imageElement) ? imageElement[0] : imageElement;

  // Concept posters use item_type + index (not design result id alone).
  if (resultId != null && itemType != null) {
    try {
      return await fetchGenerationHistoryImageBlob(resultId, {
        itemType,
        index: index == null ? 0 : index,
      });
    } catch (e) {
      console.warn('resolveImageBlob: concept poster API failed, trying next source', e);
    }
  }

  if (imgEl) {
    try {
      return await imageElementToBlob(imgEl);
    } catch (e) {
      console.warn('resolveImageBlob: imageElement failed, trying next source', e);
    }
  }

  if (imageUrl) {
    try {
      const response = await fetch(imageUrl, { mode: 'cors' });
      if (response.ok) {
        return response.blob();
      }
      console.warn(
        'resolveImageBlob: fetch imageUrl failed',
        response.status,
        imageUrl,
      );
    } catch (e) {
      console.warn('resolveImageBlob: fetch imageUrl error, trying next source', e);
    }
  }

  if (resultId != null) {
    try {
      return await fetchGenerationHistoryImageBlob(resultId);
    } catch (e) {
      console.warn('resolveImageBlob: history image API failed', e);
    }
  }

  throw new Error('copy_image_failed');
}

export async function copyImageToClipboard(imageUrl, options = {}) {
  const blob = await resolveImageBlob(imageUrl, options);
  await writeImageBlobToClipboard(blob);
}

export function removeAsterisks(text) {
  return (text || '').replace(/\*\*/g, '');
}
